import { Alert, AlertDescription, AlertTitle, Button, cn } from "@ndd/ui";
import {
  CircleAlert,
  Clock,
  Info,
  Loader2,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import React from "react";
import type {
  SessionRecapChipIcon,
  SessionRecapChipTone,
  SessionRecapChipView,
  SessionRecapPanelBody,
  SessionRecapPanelView,
} from "../../lib/session-recap-state";
import { DraftPlaceholder } from "./appointment-draft-section";
import { DRAWER_TEXT } from "./drawer-type-roles";
import { SessionRecapMarkdown } from "./session-recap-markdown";
import { SideDrawerHeader } from "./side-drawer-header";

const CHIP_ICONS: Record<SessionRecapChipIcon, LucideIcon> = {
  Clock,
  Loader2,
  Sparkles,
  CircleAlert,
  Info,
};

// Component 149:45; the pastel strokes are app @theme tokens in styles.css.
const CHIP_TONES: Record<SessionRecapChipTone, { closed: string; open: string }> = {
  muted: {
    closed:
      "border-border bg-muted text-muted-foreground hover:border-border-strong hover:bg-accent hover:text-foreground",
    open: "border-border-strong bg-sand-200 text-foreground hover:bg-sand-300",
  },
  ai: {
    closed:
      "border-recap-ai-stroke bg-ai-light text-ai-hover hover:border-ai hover:bg-ai/15",
    open: "border-ai bg-ai text-white hover:border-ai-hover hover:bg-ai-hover",
  },
  danger: {
    closed:
      "border-recap-danger-stroke bg-danger-soft text-danger hover:border-danger hover:bg-danger/15",
    open: "border-danger bg-danger text-white hover:border-danger/90 hover:bg-danger/90",
  },
};

export function sessionRecapChipClassName(
  tone: SessionRecapChipTone,
  open: boolean
): string {
  return cn(
    "inline-flex cursor-pointer items-center gap-1.5 rounded-full border py-[3px] pl-2 pr-2.5 text-xs font-medium leading-4 transition-colors",
    open ? CHIP_TONES[tone].open : CHIP_TONES[tone].closed
  );
}

/** The Badge-shaped toggle beside the patient name; it carries the Recap state. */
export function SessionRecapChip({
  view,
  open,
  panelId,
  onToggle,
}: {
  view: Extract<SessionRecapChipView, { visible: true }>;
  open: boolean;
  panelId: string;
  onToggle: () => void;
}): React.ReactElement {
  const Icon = CHIP_ICONS[view.icon];

  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={panelId}
      onClick={onToggle}
      className={sessionRecapChipClassName(view.tone, open)}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          "size-[13px] shrink-0",
          view.icon === "Loader2" && "animate-spin"
        )}
      />
      {view.label}
    </button>
  );
}

/** Drawer or Sheet content: the shared drawer header over one state body. */
export function SessionRecapPanel({
  view,
  onClose,
  onRetry,
}: {
  view: SessionRecapPanelView;
  /** Present when this panel owns its close control (both the drawer and the Sheet). */
  onClose?: () => void;
  onRetry?: () => void;
}): React.ReactElement {
  return (
    <section
      aria-label="Session recap"
      className="flex h-full min-h-0 flex-col bg-card"
    >
      <SideDrawerHeader
        title="Session recap"
        subtitle={view.subtitle}
        closeLabel="Close session recap"
        onClose={onClose}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <PanelBody body={view.body} onRetry={onRetry} />
      </div>
    </section>
  );
}

function offersRetry(body: SessionRecapPanelBody, onRetry?: () => void): boolean {
  return body.kind === "failed" && body.canRetry && onRetry !== undefined;
}

function PanelBody({
  body,
  onRetry,
}: {
  body: SessionRecapPanelBody;
  onRetry?: () => void;
}): React.ReactElement {
  switch (body.kind) {
    case "waiting":
      return <WaitingBody />;
    case "generating":
      return <GeneratingBody streamedText={body.streamedText} />;
    case "completed":
      return (
        <div className="px-5 pb-12 pt-1">
          <SessionRecapMarkdown markdown={body.markdown} />
        </div>
      );
    case "failed":
    case "exhausted":
      return (
        <Alert variant={body.alert} className="m-4 w-auto">
          <AlertTitle>{body.copy.title}</AlertTitle>
          <AlertDescription className="mt-1">
            {body.copy.description}
            {offersRetry(body, onRetry) ? (
              <div className="mt-2">
                <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                  Try again
                </Button>
              </div>
            ) : null}
          </AlertDescription>
        </Alert>
      );
  }
}

function WaitingBody(): React.ReactElement {
  return (
    <div className="px-5 py-7 text-center">
      <span className="mx-auto flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Clock aria-hidden="true" className="size-[18px]" />
      </span>
      <p className={cn(DRAWER_TEXT.stateTitle, "mt-3")}>
        Your recap arrives after the visit transcript
      </p>
      <p className={cn(DRAWER_TEXT.stateBody, "mt-1")}>
        Waiting for the transcript from Healthie · usually 1–2 minutes after the visit
        ends. The recap starts on its own. Nothing to do.
      </p>
    </div>
  );
}

function preventCopy(event: React.SyntheticEvent): void {
  event.preventDefault();
}

function GeneratingBody({
  streamedText,
}: {
  streamedText: string;
}): React.ReactElement {
  return (
    <>
      <div
        role="status"
        className="flex items-start gap-2.5 border-b border-ai/20 bg-ai-soft px-4 py-3 text-body leading-5 text-foreground"
      >
        <Loader2
          aria-hidden="true"
          className="mt-0.5 size-3.5 shrink-0 animate-spin text-ai-hover"
        />
        <span>
          <span className="font-medium">Writing your recap…</span> usually under a
          minute. Nothing to do, and your notes aren’t affected.
        </span>
      </div>
      <div
        role="region"
        aria-label="Recap preview, locked while writing"
        onCopy={preventCopy}
        onCut={preventCopy}
        onContextMenu={preventCopy}
        className="select-none"
      >
        {streamedText !== "" ? (
          <div className="px-5 pt-1">
            <SessionRecapMarkdown markdown={streamedText} />
          </div>
        ) : null}
        <DraftPlaceholder />
      </div>
    </>
  );
}
